import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SquareCheckBigIcon, SquareIcon, TimerIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSidePanel } from '@/shared/components/side-panel';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';
import { useAuthStore } from '@/shared/auth/store';
import { Button } from '@/shared/ui/button';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { invalidateTask, taskKeys, tasksApi, useMyTimer, type TaskStatus } from '../api';
import { clock } from '../model/duration';
import { STATUS_META } from '../model/status';

import { DeadlineText, StatusBadge } from './bits';

/** Top bar: the running timer (task number and elapsed time); click opens the task, the square stops it. */
export function TaskTimerWidget() {
  const { t } = useTranslation('tasks');
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  const timer = useMyTimer({ enabled: signedIn });
  const { open } = useSidePanel();
  const queryClient = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  const running = timer.data;
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);
  const stop = useMutation({
    mutationFn: (taskId: string) => tasksApi.stopTimer(taskId),
    onSuccess: (time, taskId) => {
      queryClient.setQueryData(taskKeys.time(taskId), time);
      queryClient.setQueryData(taskKeys.timer, null);
      invalidateTask(queryClient, taskId);
    },
  });
  if (!running) return null;
  const elapsed = Math.max(0, Math.floor((now - new Date(running.startedAt).getTime()) / 1000));
  return (
    <div
      className="flex items-center rounded-md bg-brand-soft text-brand-text"
      data-testid="task-timer-widget"
    >
      <SimpleTooltip content={t('timer.open', { number: running.number, title: running.title })}>
        <button
          type="button"
          onClick={() => open('task', running.taskId)}
          className="flex h-8 items-center gap-1.5 rounded-l-md pr-1 pl-2 text-sm font-medium focus-ring"
          aria-label={t('timer.open', { number: running.number, title: running.title })}
        >
          <TimerIcon className="size-4" />
          <span className="tabular-nums">{clock(elapsed)}</span>
          <span className="hidden text-xs opacity-80 md:inline">#{running.number}</span>
        </button>
      </SimpleTooltip>
      <Button
        variant="ghost"
        size="icon-xs"
        className="mr-0.5 text-brand-text hover:bg-brand/10"
        aria-label={t('time.stop')}
        onClick={() => stop.mutate(running.taskId)}
        loading={stop.isPending}
      >
        {!stop.isPending && <SquareIcon className="size-3 fill-current" />}
      </Button>
    </div>
  );
}

/** Global search result of entity type `task` (content of the search page's link row; meta: number, status, groupName, deadline). */
export function TaskSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const meta = result.meta ?? {};
  const status = meta.status && meta.status in STATUS_META ? (meta.status as TaskStatus) : undefined;
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <>
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-brand-text shadow-xs">
        <SquareCheckBigIcon className="size-[1.125rem]" strokeWidth={1.8} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex min-w-0 items-center gap-2">
          {meta.number && <span className="font-mono text-xs text-muted-foreground">#{meta.number}</span>}
          <span className="truncate text-[0.9375rem] font-medium text-foreground">
            {highlights?.title ?? result.title}
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {status && <StatusBadge status={status} />}
          {meta.groupName && <span>{meta.groupName}</span>}
          {meta.deadline && <DeadlineText deadline={meta.deadline} isOverdue={false} />}
        </span>
        {snippet && <span className="line-clamp-2 text-sm text-muted-foreground">{snippet}</span>}
      </span>
    </>
  );
}
